import { createColumnHelper } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import { LuPlus, LuVideo } from 'react-icons/lu';

import { EventView } from '@ndvr/customer-api';

import { CalendarIcon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import {
    convertBackendTimeToBrowser,
    formatDate,
    formatTime,
    getHouseholdName,
    titleCase,
    useRequiredParams,
} from 'utils';
import { NewEventSheet } from 'views/calendar/widgets/NewEventSheet';
import { useCommandCenterHouseholdProfile } from 'views/household/household-queries';
import { useListHouseholdEvents } from 'views/household/HouseholdEvents/events-queries';
import { HouseholdHeaderActionsPortal } from 'views/household/HouseholdIndividualPage';
import { CustomTablePage } from 'widgets';

import { EventDetailsDrawer } from './EventDetailsDrawer';

const columnHelper = createColumnHelper<EventView>();

export const HouseholdEventsTab = () => {
    const { globalAccountGUID: householdGUID } = useRequiredParams(['globalAccountGUID']);
    const [selectedEvent, setSelectedEvent] = useState<EventView | null>(null);
    const [isSheetOpen, setIsSheetOpen] = useState(false);
    const [isNewEventSheetOpen, setIsNewEventSheetOpen] = useState(false);

    const { data: householdEvents, isLoading: isLoadingEvents } = useListHouseholdEvents({ householdGUID });
    const { data: householdProfile } = useCommandCenterHouseholdProfile(householdGUID);

    const defaultHousehold = householdProfile
        ? {
              value: householdProfile.globalAccount.globalAccountGUID,
              label: getHouseholdName(householdProfile.globalAccount) || 'Unnamed Household',
          }
        : undefined;

    const columns = useMemo(
        () => [
            columnHelper.accessor('title', {
                header: 'Event',
                cell: ({ row }) => {
                    const event = row.original;
                    return (
                        <div className="flex items-center gap-2">
                            <CalendarIcon className="h-4 w-4 flex-shrink-0 text-green-500" />
                            <div className="min-w-0 flex-1">
                                <div className="truncate font-medium">{event.title}</div>
                                {event.description && (
                                    <div className="text-muted-foreground truncate text-sm">{event.description}</div>
                                )}
                            </div>
                        </div>
                    );
                },
            }),
            columnHelper.accessor('startTime', {
                header: 'Date & Time',
                cell: ({ row }) => {
                    const event = row.original;
                    return (
                        <div>
                            <div className="text-sm">
                                {formatDate(convertBackendTimeToBrowser(event.startTime, event.startTimeZone))}
                            </div>
                            <div className="text-muted-foreground text-xs">
                                {formatTime(convertBackendTimeToBrowser(event.startTime, event.startTimeZone))} -{' '}
                                {formatTime(convertBackendTimeToBrowser(event.endTime, event.endTimeZone))}
                            </div>
                        </div>
                    );
                },
            }),
            columnHelper.accessor('provider', {
                header: 'Location',
                cell: ({ row }) => {
                    const event = row.original;
                    return (
                        <div className="flex items-center gap-1 text-sm">
                            {event.url && (
                                <>
                                    <LuVideo className="h-3 w-3" />
                                    <span>{titleCase(event.provider)}</span>
                                </>
                            )}
                        </div>
                    );
                },
            }),
            columnHelper.accessor((row) => row.participants?.length ?? 0, {
                id: 'attendees',
                header: 'Attendees',
                cell: ({ row }) => {
                    const event = row.original;
                    return (
                        <div className="text-muted-foreground text-sm">
                            {event.participants.length > 0
                                ? event.participants.map((p) => p.name || p.email).join(', ')
                                : 'No attendees listed'}
                        </div>
                    );
                },
            }),
        ],
        [],
    );

    return (
        <div>
            <HouseholdHeaderActionsPortal>
                <div className="flex items-center gap-4">
                    <Button variant="outline2" size="sm" onClick={() => setIsNewEventSheetOpen(true)}>
                        <LuPlus className="h-4 w-4" />
                        <span>Event</span>
                    </Button>
                </div>
            </HouseholdHeaderActionsPortal>

            <CustomTablePage
                data={householdEvents ?? []}
                columns={columns}
                isLoading={isLoadingEvents}
                rowOnClick={(event) => {
                    setSelectedEvent(event);
                    setIsSheetOpen(true);
                }}
                exportName="events"
                cardClassName="m-0 border-0 shadow-none"
                defaultSortCol="startTime"
                defaultSortDirection="desc"
            />

            <EventDetailsDrawer event={selectedEvent} isOpen={isSheetOpen} onOpenChange={setIsSheetOpen} />
            <NewEventSheet
                open={isNewEventSheetOpen}
                onOpenChange={setIsNewEventSheetOpen}
                defaultHousehold={defaultHousehold}
                disableHouseholdSelection={true}
            />
        </div>
    );
};
